import React, { useCallback, useEffect, useMemo, useState } from "react";

import { Grid, MenuItem, SelectChangeEvent } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import * as yup from "yup";

import useYupValidation from "../../../../../../../hooks/useYupValidation";
import { Measurement } from "../../../../../../../models/measurement";
import { PriceGroup } from "../../../../../../../models/price.group";
import { ChargeOptionsEnum, Service as ServiceModel } from "../../../../../../../models/service";
import PriceGroupService from "../../../../../../../services/price.group.service";
import Service from "../../../../../../../services/service.booking.service";
import { useAppState } from "../../../../../../../context/StateContext";
import { Status } from "../../../../../../../util/enums";
import { handleValidation } from "../../../../../../../util/formValidation";
import { getMeasurement } from "../../../../../../../util/getMeasurement";
import { getPriceStringValid, formatCurrency } from "../../../../../../../util/prices";
import AutoSuggest from "../../../../../../../components/Form/AutoSuggest";
import Input from "../../../../../../../components/Form/Input";
import Select from "../../../../../../../components/Form/Select";
import Modal from "../../../../../../../components/Generic/Modal";
import SkeletonComponent from "../../../../../../../components/Generic/Skeleton";
import useStyles from "./styles";

interface Props {
  serviceToUpdate?: ServiceModel;
  hasError?: boolean;
  open: boolean;
  isOnViewMode: boolean;
  isLoading: boolean;
  closeModal: () => void;
  submit: (service: any) => void;
}

const ServiceModal = ({
  serviceToUpdate,
  hasError,
  open,
  isOnViewMode,
  isLoading,
  closeModal,
  submit,
}: Props) => {
  const classes = useStyles();
  const { currentUser } = useAppState();

  const measurement = getMeasurement();

  const { t: translate } = useTranslation();

  const [sellTypesList, setSellTypesList] = useState<string[]>([]);

  const [code, setCode] = useState<string>("");
  const [sellDescription, setSellDescription] = useState<string>("");
  const [sellType, setSellType] = useState<string>("");
  const [m3Fee, setM3Fee] = useState<string>("");
  const [flatFee, setFlatFee] = useState<string>("");
  const [hourlyFee, setHourlyFee] = useState<string>("");
  const [halfHourFee, setHalfHourFee] = useState<string>("");
  const [status, setStatus] = useState<string>(Status.INACTIVE);
  const [defaultFee, setDefaultFee] = useState<string>("");
  const [groups, setGroups] = useState<PriceGroup[]>([]);
  const [margin, setMargin] = useState<string>("0");
  const [hasFormBeenSubmitted, setHasFormBeenSubmitted] = useState<boolean>(false);

  const [allGroups, setAllGroups] = useState<PriceGroup[]>([]);
  const [errors, setErrors] = useState<yup.ValidationError[]>([]);

  const { getErrorByField } = useYupValidation(errors);

  const schema = yup.object().shape({
    code: yup.string().required(translate("thisFieldIsRequired")),
    sellDescription: yup.string().required(translate("thisFieldIsRequired")),
  });

  const clearFields = () => {
    setCode("");
    setSellDescription("");
    setSellType("");
    setM3Fee("0");
    setFlatFee("0");
    setHalfHourFee("0");
    setHourlyFee("0");
    setStatus(Status.INACTIVE);
    setDefaultFee("");
    setGroups([]);
    setMargin("");
  };

  const validateObject = (service?: ServiceModel) => ({
    code: service?.code,
    sellDescription: service?.sellDescription,
  });

  const setValueFormattedForInput = (value?: string) => {
    const currency = currentUser?.company?.currency;

    if (!value) return formatCurrency(0, currency, false);

    return formatCurrency(value, currency, false);
  };

  const handleSubmit = () => {
    if (!hasFormBeenSubmitted) setHasFormBeenSubmitted(true);

    const object = {
      code,
      sellDescription,
      sellType,
      sellM3Fee: Number(setFeePrices(m3Fee)),
      sellFlatFee: Number(setFeePrices(flatFee)),
      sellHalfHourFee: Number(setFeePrices(halfHourFee)),
      sellHourlyFee: Number(setFeePrices(hourlyFee)),
      status,
      defaultFee,
      groups,
      margin: Number(margin),
    };

    const validateSyncObject = validateObject(object);

    if (
      handleValidation({
        validateSyncObject,
        schema,
        updateErrors: setErrors,
      })
    )
      submit(object);
  };

  const setFeePrices = (value: string) => {
    const decimalSeparator = currentUser?.company?.currency?.decimalSeparator ?? ".";
    let newValue = value;

    if (newValue.includes(",") && newValue.includes(".") && decimalSeparator === ",") {
      newValue = value.replace(".", "");
    }

    const withPointer = getPriceStringValid(newValue, decimalSeparator);

    return withPointer;
  };

  const validateFeePrice = (value: string, setCallback: (value: string) => void) => {
    if (/\d+[.|,]?$/g.test(value) || value === "") {
      const decimalSeparator = currentUser?.company?.currency?.decimalSeparator ?? ".";
      const decimalPoints = currentUser?.company?.currency?.decimalPoints ?? 2;

      if (value.includes(decimalSeparator)) {
        const isValid = value.split(decimalSeparator);

        if (isValid[1].length <= decimalPoints) setCallback(value);
      } else {
        setCallback(value);
      }
    }
  };

  const { isFetching: loadingGroups } = useQuery(
    ["groups.list"],
    () =>
      PriceGroupService.getAll({
        companyId: currentUser?.companyId,
        sortBy: JSON.stringify([["name", "asc"]]),
        status: Status.ACTIVE,
      }),
    {
      enabled: !!currentUser?.companyId,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data && response.data instanceof Array)
          setAllGroups(response.data);
      },
    }
  );

  useQuery(["service.type.list"], () => Service.getTypes(), {
    enabled: !!currentUser?.id,
    refetchOnWindowFocus: false,
    onSuccess: (response) => {
      if (response && response.data?.length > 0 && response.data instanceof Array) {
        setSellTypesList(response.data);
      }
    },
  });

  const getDimensionsFeeOption = useCallback((measurement?: Measurement) => {
    const fee = translate("DimensionFee").replace("{dimension}", measurement?.code || "");
    const split = fee.split(" ");

    return `${split[0]?.toUpperCase()} ${split[1]}`;
  }, []);

  const priceOptions = useMemo(() => {
    return [
      { key: ChargeOptionsEnum.Fee, label: getDimensionsFeeOption(measurement) },
      { key: ChargeOptionsEnum.Flat, label: translate("flatFee") },
      { key: ChargeOptionsEnum.Hourly, label: translate("hourlyFee") },
      { key: ChargeOptionsEnum["Half Hour"], label: translate("halfHourFee") },
    ];
  }, [measurement]);

  const modalTitle = useMemo(() => {
    if (serviceToUpdate) {
      if (isOnViewMode) return translate("viewService");

      return translate("editService");
    }

    return translate("createService");
  }, [serviceToUpdate, isOnViewMode]);

  useEffect(() => {
    if (serviceToUpdate) {
      setCode(serviceToUpdate.code || "");
      setSellDescription(serviceToUpdate.sellDescription || "");
      setSellType(serviceToUpdate.sellType || "");
      setM3Fee(serviceToUpdate.sellM3Fee ? String(serviceToUpdate.sellM3Fee) : "0");
      setFlatFee(serviceToUpdate.sellFlatFee ? String(serviceToUpdate.sellFlatFee) : "0");
      setHalfHourFee(
        serviceToUpdate.sellHalfHourFee ? String(serviceToUpdate.sellHalfHourFee) : "0"
      );
      setHourlyFee(serviceToUpdate.sellHourlyFee ? String(serviceToUpdate.sellHourlyFee) : "0");
      setStatus(serviceToUpdate.status || "");
      setDefaultFee(serviceToUpdate.defaultFee || "");
      setGroups(serviceToUpdate.groups || []);
      setMargin(String(serviceToUpdate.margin) || "");
    }
  }, [serviceToUpdate]);

  useEffect(() => {
    if (hasError !== undefined && !hasError) {
      clearFields();
      closeModal();
    }
  }, [hasError]);

  useEffect(() => {
    if (hasFormBeenSubmitted) {
      const object = {
        code,
        sellDescription,
        sellType,
        sellM3Fee: Number(setFeePrices(m3Fee)),
        sellFlatFee: Number(setFeePrices(flatFee)),
        sellHalfHourFee: Number(setFeePrices(halfHourFee)),
        sellHourlyFee: Number(setFeePrices(hourlyFee)),
        status,
        defaultFee,
        groups,
        margin: Number(margin),
      };

      const validateSyncObject = validateObject(object);

      handleValidation({
        validateSyncObject,
        schema,
        updateErrors: setErrors,
      });
    }
  }, [
    hasFormBeenSubmitted,
    code,
    sellDescription,
    sellType,
    m3Fee,
    flatFee,
    halfHourFee,
    hourlyFee,
    status,
    defaultFee,
    groups,
    margin,
  ]);

  return (
    <Modal
      open={open}
      handleClose={closeModal}
      title={modalTitle}
      dataCyTitle="serviceModalTitle"
      className={classes.modalWidth}
      showModalBodyScroll={false}
      data-cy="serviceModal"
      showActionButtons
      actionButtonProps={{
        dataCyCancel: "cancelButton",
        dataCySave: "submitButton",
        onCancel: () => {
          clearFields();
          closeModal();
        },
        loading: isLoading,
        saveDisabled: isLoading,
        textSave: serviceToUpdate ? translate("update") : translate("create"),
        onSave: isOnViewMode ? undefined : () => handleSubmit(),
      }}
      labelStatus={translate("status")}
      showStatusField={true}
      statusFieldProps={{
        label: translate("serviceStatus"),
        value: status,
        disabled: isOnViewMode,
        dataCy: "selectStatus",
        placeholder: translate("selectAnOption"),
        handleChange: (value) => setStatus(value),
      }}
    >
      <section className={classes.modalSection}>
        <Grid className="mb-2" container direction="row" spacing={3}>
          <Grid item md={6} lg={6}>
            <Input
              disabled={isOnViewMode}
              name="code"
              label={translate("code")}
              placeholder="123"
              error={!!getErrorByField("code")}
              errorMessage={getErrorByField("code")}
              value={code}
              onChange={(event) => setCode(event.target.value)}
            />
          </Grid>
        </Grid>

        <Grid className="mb-2" container direction="row" spacing={3}>
          <Grid item md={6} lg={6}>
            <Input
              disabled={isOnViewMode}
              name="sellDescription"
              label={translate("serviceDescription")}
              placeholder="123"
              error={!!getErrorByField("sellDescription")}
              errorMessage={getErrorByField("sellDescription")}
              value={sellDescription}
              onChange={(event) => setSellDescription(event.target.value)}
            />
          </Grid>

          <Grid item md={6} lg={6}>
            <Select
              disabled={isOnViewMode}
              data-cy="sellType"
              name="sellType"
              label={translate("sellType")}
              error={!!getErrorByField("sellType")}
              errorMessage={getErrorByField("sellType")}
              value={sellType}
              onChange={(event: SelectChangeEvent<any>) => setSellType(event.target.value)}
              placeholder={translate("selectAnOption")}
              displayEmpty
            >
              {sellTypesList.map((type: string) => (
                <MenuItem data-cy={type} key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </Select>
          </Grid>
        </Grid>

        <Grid container direction="row" spacing={3}>
          <Grid item md={6} lg={6}>
            <Input
              disabled={isOnViewMode}
              name="m3fee"
              label={getDimensionsFeeOption(measurement)}
              leftIcon={currentUser?.company?.currency?.symbol ?? "$"}
              placeholder="0.00"
              error={!!getErrorByField("m3Fee")}
              errorMessage={getErrorByField("m3Fee")}
              value={m3Fee}
              onFocus={() => setM3Fee(String(setValueFormattedForInput(m3Fee)))}
              onChange={(event) => validateFeePrice(event.target.value, setM3Fee)}
            />
          </Grid>

          <Grid item md={6} lg={6}>
            <Input
              disabled={isOnViewMode}
              name="flatFee"
              label={translate("flatFee")}
              leftIcon={currentUser?.company?.currency?.symbol ?? "$"}
              placeholder="0.00"
              error={!!getErrorByField("flatFee")}
              errorMessage={getErrorByField("flatFee")}
              value={flatFee}
              onChange={(event) => validateFeePrice(event.target.value, setFlatFee)}
              onFocus={() => setFlatFee(String(setValueFormattedForInput(flatFee)))}
            />
          </Grid>
        </Grid>

        <Grid container direction="row" spacing={3}>
          <Grid item md={6} lg={6}>
            <Input
              disabled={isOnViewMode}
              name="hourlyFee"
              label={translate("hourlyFee")}
              leftIcon={currentUser?.company?.currency?.symbol ?? "$"}
              placeholder="0.00"
              error={!!getErrorByField("hourlyFee")}
              errorMessage={getErrorByField("hourlyFee")}
              value={hourlyFee}
              onChange={(event) => validateFeePrice(event.target.value, setHourlyFee)}
              onFocus={(event) => {
                setHourlyFee(String(setValueFormattedForInput(event.target.value)));
              }}
            />
          </Grid>

          <Grid item md={6} lg={6}>
            <Input
              disabled={isOnViewMode}
              name="halfHourFee"
              label={translate("halfHourFee")}
              placeholder="0.00"
              leftIcon={currentUser?.company?.currency?.symbol ?? "$"}
              error={!!getErrorByField("halfHourFee")}
              errorMessage={getErrorByField("halfHourFee")}
              value={halfHourFee}
              onChange={(event) => validateFeePrice(event.target.value, setHalfHourFee)}
              onFocus={() => setHalfHourFee(String(setValueFormattedForInput(halfHourFee)))}
            />
          </Grid>
        </Grid>

        <Grid container direction="row" spacing={3}>
          <Grid item md={6} lg={6}>
            <Select
              disabled={isOnViewMode}
              data-cy="defaultFee"
              name="defaultFee"
              label={translate("defaultPrice")}
              value={defaultFee}
              onChange={(event: SelectChangeEvent<any>) => setDefaultFee(event.target.value)}
              placeholder={translate("selectAnOption")}
              displayEmpty
            >
              {priceOptions.map((option) => (
                <MenuItem data-cy={`option-${option.key}`} key={option.key} value={option.key}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </Grid>
          <Grid item md={6} lg={6}>
            <Input
              disabled={isOnViewMode}
              name="margin"
              label={translate("margin")}
              rightIcon={"%"}
              placeholder="0"
              error={!!getErrorByField("margin")}
              errorMessage={getErrorByField("margin")}
              type="number"
              min="0"
              value={margin}
              onChange={(event) => {
                const replaced = event.target.value.replace(/\D/g, "");

                if (Number.isNaN(Number(replaced))) {
                  setMargin("0");
                } else setMargin(replaced);
              }}
            />
          </Grid>
        </Grid>

        <Grid container direction="row" spacing={3}>
          <SkeletonComponent isLoading={loadingGroups} classesName="mt-2">
            <Grid item xs={12} md={12} lg={12}>
              <AutoSuggest
                tagDataCy="item-group"
                disabled={isOnViewMode}
                data-cy="groupSuggest"
                label={translate("groups")}
                placeholder={translate("searchGroups")}
                options={allGroups}
                value={groups}
                getOptionLabel={(group: PriceGroup) => (group ? group.name || "" : "")}
                getOptionSelected={(option: PriceGroup, value: PriceGroup) =>
                  option.id === value.id
                }
                multiple
                onChange={(groups: PriceGroup[]) => setGroups(groups)}
              />
            </Grid>
          </SkeletonComponent>
        </Grid>
      </section>
    </Modal>
  );
};

export default ServiceModal;
